import React from 'react';
import {
    BudgetBys,
    BudgetExpenseOrIncome,
    GESTIONNAIRE_FIELD,
} from 'business/constants';
import { translateBlockValue } from 'i18n';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import {
    ORG_CHART_FINANCE_NAME_FIELD,
    SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
} from 'routes/BudgetModule/config/constants';
import { getSumOfMetric } from 'routes/BudgetModule/helpers/dataOperation';
import { getFormat } from 'routes/BudgetModule/helpers/format';
import { ChartSettingsT } from 'business/definitions';
import {
    BudgetProposalT,
    BudgetVentilatedDataT,
    BudgetVentilatedDataWithCadrageT,
} from 'routes/BudgetModule/config/types';
import { BudgetProposalVentilationBy } from 'routes/BudgetModule/config/queries';
import PieChart from './PieChart';
import { addSpacesToResourceDoublons } from '../helpers/dataHelpers';
import { Box } from '@mui/system';

const StyBudgetPieChart = styled(Box, {
    name: 'StyBudgetPieChart',
})({
    display: 'flex', //super important use flex for Autoflexer
    flexFlow: 'column nowrap',
    alignItems: 'center',
    width: '45%',
    height: '40vh',
});

type Props = {
    budgetProposal: BudgetProposalT;
    expenseOrIncome: BudgetExpenseOrIncome;
    settings: ChartSettingsT;
    initialData: {
        query: QueryT;
        data: BudgetVentilatedDataWithCadrageT[] | BudgetVentilatedDataT[];
    };
    ventilationType: BudgetProposalVentilationBy;
};

const StyChartTitle = styled(Typography, {
    name: 'StyChartTitle',
})(({ theme }) => ({
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '700',
    lineHeight: '21px',
    letterSpacing: '0.42px',
    textAlign: 'left',
    color: theme.palette.black,
}));

const PieChartsData: React.FC<Props> = ({
    budgetProposal,
    expenseOrIncome,
    settings,
    initialData,
    ventilationType,
}) => {
    const { t } = useTranslation();

    const metricInPieChart = expenseOrIncome.toLowerCase();

    const { data } = initialData;

    const filteredColumns: ResponseColumnT[] = [];
    if (ventilationType === 'resource') {
        filteredColumns.push({
            label: 'resourceName',
            key:
                budgetProposal.by === BudgetBys.ORG_CHART_FINANCE
                    ? ORG_CHART_FINANCE_NAME_FIELD
                    : GESTIONNAIRE_FIELD,
            type: 'category',
        });
    } else {
        filteredColumns.push({
            label: 'secondaryBudgetAxisElementName',
            key: SECONDARY_BUDGET_AXIS_ELEMENT_NAME_FIELD,
            type: 'category',
        });
    }
    filteredColumns.push({
        label: 'amount',
        key: metricInPieChart,
        type: 'integer',
    });
    const totalAmount = getSumOfMetric(data, metricInPieChart);

    // In order for EChart to differentiate doublons in the legend, we had spaces at the end of the names
    const pieData = addSpacesToResourceDoublons(data, budgetProposal.by);

    return (
        <StyBudgetPieChart>
            <StyChartTitle>
                {t('budgetProposal.expenseIncomePieChartTitle', {
                    expenseType: translateBlockValue(expenseOrIncome, true),
                    sum: getFormat(totalAmount),
                })}
            </StyChartTitle>

            <PieChart
                queryElement={initialData.query}
                data={pieData}
                columns={filteredColumns}
                settings={settings}
            />
        </StyBudgetPieChart>
    );
};

export default PieChartsData;
